import { API } from "@swivl-mono/api-helper";
import { NameHelper, filterClasses } from "@swivl-mono/helpers";
import { Channel, FeatureFlagKey, UserInterface } from "@swivl-mono/types";
import { AnimatePresence } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { FaEllipsis } from "react-icons/fa6";
import { IoChatbubbleEllipses } from "react-icons/io5";
import { MdBlock } from "react-icons/md";
import { TbExternalLink } from "react-icons/tb";
import { Link, useSearchParams } from "react-router-dom";

import { isFeatureEnabled } from "../../Helpers/FeatureFlags/isFeatureEnabled";
import CRMUsersModel from "../../Models/CRM/CRMUsers.Model";
import { Member } from "../../Models/Member/Member.Current";
import { UsersModel } from "../../Models/Users/Users.Model";
import { MoreDetailsDropdown } from "../../Views/ContactCenter/Conversations/DetailsColumn/Components/NameRow/MoreDetailsDropdown";
import UserDetailsSection from "./UserDetailsCard.DetailsSection";
import { QuickEditForm } from "./UserDetailsCard.QuickEditForm";

export enum UserDetailsCardVariant {
  COMPACT = "compact",
  STANDARD = "standard",
  DETAILED = "detailed"
}

interface UserDetailsCardProps {
  handleSetIsEditing: () => void;
  user: UserInterface;
  variant?: UserDetailsCardVariant;
}

const variantConfig = {
  [UserDetailsCardVariant.COMPACT]: {
    showConversationInline: true,
    showDivider: false,
    showEditButton: false,
    showDetailsButton: false,
    showEmail: false,
    showNameLink: true,
    showUserDetails: false,
    showQuickEditForm: false
  },
  [UserDetailsCardVariant.STANDARD]: {
    showConversationInline: false,
    showDivider: true,
    showEditButton: true,
    showDetailsButton: false,
    showEmail: true,
    showNameLink: false,
    showUserDetails: false
  },
  [UserDetailsCardVariant.DETAILED]: {
    showDivider: true,
    showEmail: true,
    showEditButton: true,
    showDetailsButton: true,
    showConversationInline: false,
    showNameLink: true,
    showUserDetails: true
  }
};

const UserDetailsCard = ({ handleSetIsEditing, user, variant = UserDetailsCardVariant.STANDARD }: UserDetailsCardProps) => {
  const { sessions } = UsersModel.useState((s) => s);
  const [showDetails, setShowDetails] = useState(false);
  const [showQuickEditForm, setShowQuickEditForm] = useState(false);
  const [searchParams] = useSearchParams();

  // The Suppressions link applies this user as the filter and carries the CRM sidebar's
  // org/group/location selection over to the Sequences Suppressions page.
  const suppressionsSearch = useMemo(() => {
    const params = new URLSearchParams();
    for (const key of ["groupId", "locationId", "viewAll"]) {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    }
    params.set("user", `#${user.id}`);
    // Lands on the not-sent slice (the Not sent tile preselected, the By reason card shown).
    params.set("notSent", "1");
    return `?${params.toString()}`;
  }, [searchParams, user.id]);

  useEffect(() => {
    UsersModel.loadForUser(user.id);
  }, [user.id]);

  const sessionsForUser = useMemo(() => {
    return sessions ? Object.values(sessions)?.filter((session) => session.userId === user.id) : null;
  }, [sessions, user.id]);

  const isStandard = variant === UserDetailsCardVariant.STANDARD;

  const handleEditClick = () => {
    setShowQuickEditForm(true);
  };

  const handleCancelEdit = () => {
    setShowQuickEditForm(false);
  };

  const handleEditUser = async (updatedUser: Partial<UserInterface>) => {
    if (!updatedUser.id) return;

    const updated = await API.Users.updateUser(Member.orgId!, updatedUser.id, updatedUser);

    // update usersmodel state
    UsersModel.setUserSessionOrEscalation([updated], undefined, undefined);

    // reload crm data
    CRMUsersModel.invalidateCache();
    await CRMUsersModel.load(true);

    setShowQuickEditForm(false);
  };

  const userName = NameHelper.nameForUser(user);

  return (
    <>
      <div className="rounded-sm border border-grey-300 bg-white p-4">
        <AnimatePresence initial={false} mode="wait">
          {showQuickEditForm ? (
            <QuickEditForm handleCancelEdit={handleCancelEdit} handleEditUser={handleEditUser} handleSetIsEditing={handleSetIsEditing} user={user} />
          ) : (
            <>
              {/* header */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col text-left">
                  {variantConfig[variant].showNameLink ? (
                    <Link
                      className="flex items-center gap-1 font-poppins font-semibold underline"
                      to={`/crm?locationId=${user?.locationId}&groupId=${user?.defaultGroupId}&userId=${user?.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {userName} <TbExternalLink className="size-4" />
                    </Link>
                  ) : (
                    <h2 className="font-poppins font-semibold">{userName}</h2>
                  )}
                  <p className="text-sm">{user?.phoneNumber}</p>
                  {variantConfig[variant].showEmail && <p className="text-sm">{user?.email}</p>}
                </div>
                {/* edit button and showdetails ellipsis button */}
                {variantConfig[variant].showEditButton && (
                  <div className="relative flex h-fit items-center gap-1">
                    <button className="cursor-pointer text-sm font-semibold text-grey-600" onClick={handleEditClick}>
                      Edit
                    </button>
                    {variantConfig[variant].showDetailsButton && (
                      <button
                        className={filterClasses(
                          showDetails && "ring ring-orange-500",
                          "cursor-pointer rounded-full p-1 transition-all duration-100 ease-in-out hover:text-grey-400"
                        )}
                        onClick={() => setShowDetails(!showDetails)}
                      >
                        <FaEllipsis className="h-3.5 rotate-90" />
                      </button>
                    )}

                    {/* more details dropdown */}
                    {showDetails && (
                      <div className="absolute top-0 right-0 mt-6 flex w-48 rounded-sm border border-grey-200 bg-white p-2 text-sm">
                        <MoreDetailsDropdown setShowDetails={setShowDetails} user={user} />
                      </div>
                    )}
                  </div>
                )}

                {/* conversation link - compact positioning */}
                {variantConfig[variant].showConversationInline && (
                  <Link
                    className="flex items-center gap-1 text-sm font-semibold text-orange-500"
                    to={`/contact-center/conversations/${user?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IoChatbubbleEllipses className="size-4" /> Conversation
                  </Link>
                )}
              </div>

              {/* divider */}
              {variantConfig[variant].showDivider && <div className="my-2 h-px w-full bg-grey-200" role="separator" />}

              {/* conversation link - standard positioning */}
              {!variantConfig[variant].showConversationInline && isStandard && (
                <>
                  <Link
                    className="flex items-center gap-1 text-sm font-semibold text-orange-500"
                    to={`/contact-center/conversations/user/${user?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IoChatbubbleEllipses className="size-4" /> Conversation
                  </Link>
                  {/* The Sequences page (Snowy + sequences_page, like its CRM tab — ENG-3542), filtered to this user
                      and opened on the not-sent slice (the former Suppressions page, absorbed — ENG-3811); keeps the CRM sidebar scope. */}
                  {isFeatureEnabled(FeatureFlagKey.Snowy) && isFeatureEnabled(FeatureFlagKey.SequencesPage) && (
                    <Link
                      className="mt-1 flex items-center gap-1 text-sm font-semibold text-orange-500"
                      to={{ pathname: "/crm/sequence-events", search: suppressionsSearch }}
                    >
                      <MdBlock className="size-4" /> Sequence suppressions
                    </Link>
                  )}
                </>
              )}

              {/* details section */}
              {variantConfig[variant].showUserDetails && <UserDetailsSection sessions={sessionsForUser} user={user} />}
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default UserDetailsCard;
